'use client';

// ============================================================================
// CrewAhead Maritime Platform - useTaxonomy Hook
// ============================================================================
// Fetches taxonomy data via the TaxonomyAdapter with TanStack Query caching.
// Single registry request is cached for 1 hour and shared across all
// components. Background refetch keeps data fresh.

import { useQuery } from '@tanstack/react-query';
import type { useQueryClient } from '@tanstack/react-query';
import {
  taxonomyAdapter,
  getTaxonomy,
  type TaxonomyCache,
  type TaxonomyCategory,
  type TaxonomyItem,
} from '@/lib/adapters/taxonomy-adapter';
import { QUERY_KEYS } from '@/lib/types/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface UseTaxonomyOptions {
  /** Whether the query is enabled (default: true) */
  enabled?: boolean;
  /** Time in ms before data is considered stale (default: 1 hour) */
  staleTime?: number;
  /** Time in ms before inactive cache is garbage collected (default: 24 hours) */
  gcTime?: number;
}

// ─── Primary Hook ────────────────────────────────────────────────────────────

/**
 * Hook to fetch the full taxonomy registry with caching.
 *
 * Benefits:
 * 1. Fetched once, shared across all components via React Query
 * 2. Adapter-level cache + React Query cache = double-layer caching
 * 3. No duplicate requests (React Query deduplication)
 * 4. Automatic background refresh when stale
 * 5. Graceful fallback to empty data on error
 *
 * @example
 * ```tsx
 * const { data: taxonomy, isLoading } = useTaxonomy();
 *
 * if (isLoading) return <Spinner />;
 *
 * return (
 *   <Select options={taxonomy.ranks} />
 * );
 * ```
 */
export function useTaxonomy(options: UseTaxonomyOptions = {}) {
  return useQuery<TaxonomyCache, Error>({
    queryKey: QUERY_KEYS.TAXONOMY.REGISTRY,
    queryFn: getTaxonomy,
    staleTime: options.staleTime ?? 300_000, // 5 minutes — keeps in-session data
    gcTime: options.gcTime ?? 3_600_000, // 1 hour
    enabled: options.enabled ?? true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 30_000),
  });
}

// ─── Specialized Hooks ───────────────────────────────────────────────────────

/**
 * Hook to access a single taxonomy item by category and code.
 *
 * @param category - Taxonomy category (e.g., 'ranks')
 * @param code - Item code (e.g., 'chief_officer')
 * @param options - Query options
 *
 * @example
 * ```tsx
 * const { item, isLoading } = useTaxonomyItem('ranks', 'chief_officer');
 * // item → { code: 'chief_officer', label: 'Chief Officer', ... }
 * ```
 */
export function useTaxonomyItem(
  category: TaxonomyCategory,
  code: string,
  options: Omit<UseTaxonomyOptions, 'enabled'> & { enabled?: boolean } = {}
) {
  const { data: taxonomy, isLoading, error } = useTaxonomy({
    ...options,
    enabled: options.enabled ?? !!code,
  });

  const item: TaxonomyItem | null =
    taxonomy && category in taxonomy
      ? (taxonomy[category] as TaxonomyItem[]).find((i) => i.code === code) ?? null
      : null;

  return { item, isLoading, error };
}

/**
 * Hook to access all items in a taxonomy category.
 *
 * @param category - Taxonomy category (e.g., 'vesselTypes')
 * @param options - Query options
 *
 * @example
 * ```tsx
 * const { items, isLoading } = useTaxonomyCategory('vesselTypes');
 * // items → [{ code: 'container_ship', label: 'Container Ship' }, ...]
 * ```
 */
export function useTaxonomyCategory(
  category: TaxonomyCategory,
  options: Omit<UseTaxonomyOptions, 'enabled'> & { enabled?: boolean } = {}
) {
  const { data: taxonomy, isLoading, error } = useTaxonomy(options);

  const items: TaxonomyItem[] =
    taxonomy && category in taxonomy
      ? (taxonomy[category] as TaxonomyItem[])
      : [];

  return { items, isLoading, error };
}

/**
 * Hook to get a human-readable label for a taxonomy code.
 *
 * @param category - Taxonomy category
 * @param code - Item code
 * @param fallback - Fallback label if not found
 *
 * @example
 * ```tsx
 * const { label, isLoading } = useTaxonomyLabel('ranks', 'chief_officer', 'Unknown');
 * // label → 'Chief Officer'
 * ```
 */
export function useTaxonomyLabel(
  category: TaxonomyCategory,
  code: string,
  fallback: string = code
) {
  const { data: taxonomy, isLoading } = useTaxonomy({
    enabled: !!code,
  });

  const label: string =
    taxonomy && category in taxonomy
      ? (
          (taxonomy[category] as TaxonomyItem[]).find((i) => i.code === code)
            ?.label ?? fallback
        )
      : fallback;

  return { label, isLoading };
}

// ─── Imperative Helpers ──────────────────────────────────────────────────────

/**
 * Prefetch the taxonomy registry into the React Query cache.
 * Call this early (e.g., in a layout or app init) to warm the cache.
 *
 * @example
 * ```tsx
 * // In your root layout or app initialization:
 * useEffect(() => {
 *   prefetchTaxonomy(queryClient);
 * }, []);
 * ```
 */
export function prefetchTaxonomy(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  return queryClient.prefetchQuery({
    queryKey: QUERY_KEYS.TAXONOMY.REGISTRY,
    queryFn: getTaxonomy,
    staleTime: 300_000,
  });
}

/**
 * Invalidate the taxonomy cache, forcing a refetch on next access.
 * Call this after mutations that change taxonomy data.
 *
 * @example
 * ```tsx
 * const queryClient = useQueryClient();
 *
 * // After updating a rank definition:
 * invalidateTaxonomy(queryClient);
 * ```
 */
export function invalidateTaxonomy(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  taxonomyAdapter.clearCache();
  return queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TAXONOMY.REGISTRY });
}
